import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PILLAR_LABELS } from '../lib/pillars';
import {
  Trophy, Flame, Check, Target, Plus, Utensils, Info,
  TrendingUp, Wind, Scale, Award, Sparkles, MessageCircle, ArrowRight, Activity,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import PageHeader from '../components/PageHeader';
import BadgesSection from '../components/BadgesSection';
import { LEVEL_ICONS } from '../components/WellnessJourneyCard';
import api from '../services/api';

const PILLAR_META = {
  movement:  { label: PILLAR_LABELS.movement,  color: '#CCFF00' },
  recovery:  { label: PILLAR_LABELS.recovery,  color: '#22d3ee' },
  mental:    { label: PILLAR_LABELS.mental, color: '#a855f7' },
  nutrition: { label: PILLAR_LABELS.nutrition, color: '#fb923c' },
};
const PILLAR_ORDER = ['movement', 'recovery', 'nutrition', 'mental'];

const XP_ICONS = { check: Check, target: Target, plus: Plus, utensils: Utensils };
const WIN_ICONS = { 'trending-up': TrendingUp, flame: Flame, wind: Wind, scale: Scale, award: Award };

const Journey = () => {
  const navigate = useNavigate();
  const [j, setJ] = useState(null);
  const [badges, setBadges] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openLevel, setOpenLevel] = useState(null);

  useEffect(() => {
    api.getJourney()
      .then((r) => setJ(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    api.getBadges().then((r) => setBadges(r.data)).catch(() => {});
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 flex items-center justify-center min-h-[60vh]">
          <Trophy className="w-12 h-12 text-primary animate-pulse" />
        </div>
      </DashboardLayout>
    );
  }
  if (!j) {
    return (
      <DashboardLayout>
        <div className="p-6 text-center text-zinc-400">Couldn&apos;t load your Journey. Pull to refresh.</div>
      </DashboardLayout>
    );
  }

  const LevelIcon = LEVEL_ICONS[j.level.index] || Trophy;
  const limitingColor = j.limiting_pillar ? PILLAR_META[j.limiting_pillar]?.color : '#CCFF00';

  return (
    <DashboardLayout>
      <div className="p-4 md:p-8 space-y-8 max-w-4xl">
        <PageHeader
          tag="Wellness Journey"
          tagColor="#CCFF00"
          icon={Trophy}
          title="Your Journey"
          subtitle="Level up by showing up — consistency, balance and adaptation. Not calories."
          testIdPrefix="journey"
        />

        {/* HERO — level + XP progress + streak */}
        <div className="hpme-card p-6 overflow-hidden" data-testid="journey-hero">
          <div className="hpme-card-corner" />
          <div className="flex items-center gap-5">
            <div className="w-20 h-20 rounded-sm bg-primary/15 border border-primary/30 flex items-center justify-center flex-shrink-0">
              <LevelIcon className="w-10 h-10 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-secondary font-bold">Level {j.level.index + 1}</p>
              <h2 className="font-secondary font-bold text-2xl md:text-3xl text-white leading-tight" data-testid="journey-level-name">{j.level.name}</h2>
              <div className="flex items-center gap-4 mt-1">
                <span className="hpme-number text-lg text-primary">{j.total_xp} XP</span>
                {j.streak_days > 0 && (
                  <span className="flex items-center gap-1 text-orange-400 text-sm font-bold" data-testid="journey-streak">
                    <Flame className="w-4 h-4" />{j.streak_days}-day streak
                  </span>
                )}
              </div>
              {j.level?.desc && (
                <p className="text-xs text-zinc-400 leading-relaxed mt-2" data-testid="journey-level-desc">
                  {j.level.desc}
                </p>
              )}
            </div>
          </div>

          {!j.level.is_max ? (
            <div className="mt-5">
              <div className="flex justify-between text-[11px] text-zinc-500 mb-1.5">
                <span>{j.level.name}</span>
                <span>{Math.max(0, (j.level.next_xp || 0) - j.total_xp)} XP to {j.level.next_name}</span>
              </div>
              <div className="h-2.5 bg-zinc-800 rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full transition-all duration-700" style={{ width: `${j.level.pct}%` }} data-testid="journey-xp-bar" />
              </div>
              {j.level?.next_desc && (
                <p className="text-[11px] text-zinc-500 mt-2 flex items-start gap-1.5">
                  <ArrowRight className="w-3 h-3 mt-0.5 shrink-0 text-primary/70" />
                  <span><span className="text-zinc-300 font-medium">Next — {j.level.next_name}:</span> {j.level.next_desc}</span>
                </p>
              )}
            </div>
          ) : (
            <p className="mt-5 text-sm text-primary">You&apos;ve reached the top level. Now it&apos;s about keeping the system balanced.</p>
          )}

          {/* Level path — tap a node to read what that level means (mobile-friendly) */}
          <div className="mt-6 flex items-center justify-between gap-1">
            {(j.level_details || j.levels.map((name) => ({ name }))).map((lv, i) => {
              const Ic = LEVEL_ICONS[i] || Trophy;
              const reached = i <= j.level.index;
              const isOpen = openLevel === i;
              return (
                <button
                  type="button"
                  key={lv.name}
                  onClick={() => setOpenLevel(isOpen ? null : i)}
                  className="flex flex-col items-center gap-1.5 flex-1 min-w-0 focus:outline-none"
                  data-testid={`journey-level-node-${i}`}
                  aria-expanded={isOpen}
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center border transition-all ${isOpen ? 'ring-2 ring-primary/60 ' : ''}${reached ? 'bg-primary/15 border-primary/40 text-primary' : 'bg-zinc-900 border-zinc-800 text-zinc-600'}`}>
                    <Ic className="w-4 h-4" />
                  </div>
                  <span className={`text-[9px] sm:text-[10px] text-center leading-tight ${reached ? 'text-zinc-300' : 'text-zinc-600'}`}>{lv.name}</span>
                </button>
              );
            })}
          </div>

          {/* Tapped level detail — replaces the hover-only tooltip so it works on phones */}
          {openLevel !== null && j.level_details?.[openLevel] && (
            <div className="mt-3 rounded-sm border border-primary/25 bg-zinc-900/60 p-3" data-testid="journey-level-detail">
              <div className="flex items-center justify-between gap-2 mb-1">
                <p className="font-secondary font-bold text-sm text-white">
                  {j.level_details[openLevel].name}
                  {openLevel <= j.level.index ? (
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-primary">Reached</span>
                  ) : (
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-zinc-500">{j.level_details[openLevel].min_xp} XP</span>
                  )}
                </p>
                <button type="button" onClick={() => setOpenLevel(null)} className="text-zinc-500 hover:text-zinc-300 text-xs" aria-label="Close">✕</button>
              </div>
              <p className="text-xs text-zinc-400 leading-relaxed">{j.level_details[openLevel].desc}</p>
            </div>
          )}
        </div>

        {/* TODAY's XP — checklist */}
        <div className="hpme-card p-6 overflow-hidden" data-testid="journey-today">
          <div className="hpme-card-corner" />
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-secondary font-bold text-lg tracking-tight">TODAY&apos;S XP</h3>
            <span className="hpme-number text-lg text-primary" data-testid="journey-today-xp">+{j.today_xp}</span>
          </div>
          <div className="space-y-2.5">
            {j.today_breakdown.map((b) => (
              <div key={b.key} className="flex items-center gap-3" data-testid={`journey-today-${b.key}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${b.done ? 'bg-primary text-black' : 'bg-zinc-800 text-zinc-600'}`}>
                  <Check className="w-3.5 h-3.5" />
                </div>
                <span className={`text-sm flex-1 ${b.done ? 'text-zinc-200' : 'text-zinc-500'}`}>
                  {b.label}{b.count ? ` ×${b.count}` : ''}
                </span>
                <span className={`text-sm font-bold ${b.done ? 'text-primary' : 'text-zinc-700'}`}>+{b.xp}</span>
              </div>
            ))}
          </div>
          {/* What earns XP explainer */}
          <div className="mt-5 pt-4 border-t border-zinc-800">
            <p className="flex items-center gap-1.5 text-[11px] text-zinc-500 mb-2">
              <Info className="w-3.5 h-3.5" /> XP rewards showing up — any wellness action, multiple a day all count.
            </p>
            <div className="flex flex-wrap gap-2">
              {j.xp_sources.map((s) => {
                const Ic = XP_ICONS[s.icon] || Plus;
                return (
                  <span key={s.key} className="inline-flex items-center gap-1.5 text-[11px] text-zinc-400 bg-zinc-900/70 border border-zinc-800 rounded-full px-2.5 py-1">
                    <Ic className="w-3 h-3 text-zinc-500" />{s.label} <span className="text-primary font-bold">+{s.xp}</span>
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        {/* BALANCE — the unique mechanic */}
        <div className="hpme-card p-6 overflow-hidden" data-testid="journey-balance">
          <div className="hpme-card-corner" />
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-secondary font-bold text-lg tracking-tight">PILLAR BALANCE</h3>
            {j.balance_score != null && (
              <span className="hpme-number text-2xl" style={{ color: limitingColor }} data-testid="journey-balance-score">{j.balance_score}</span>
            )}
          </div>
          <p className="text-xs text-zinc-500 mb-4">Balance the system — lifting your lowest pillar moves you more than maxing your best.</p>

          <div className="space-y-3">
            {PILLAR_ORDER.map((key) => {
              const meta = PILLAR_META[key];
              const val = j.pillars?.[key];
              const has = typeof val === 'number';
              const isLimiting = key === j.limiting_pillar;
              return (
                <div key={key} data-testid={`journey-pillar-${key}`}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm flex items-center gap-2" style={{ color: has ? meta.color : '#52525b' }}>
                      {meta.label}
                      {isLimiting && has && (
                        <span className="text-[9px] uppercase tracking-wider text-zinc-900 px-1.5 py-0.5 rounded-sm font-bold" style={{ backgroundColor: meta.color }}>Lever</span>
                      )}
                    </span>
                    <span className="text-sm font-bold" style={{ color: has ? meta.color : '#52525b' }}>{has ? val : '—'}</span>
                  </div>
                  <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${has ? val : 0}%`, backgroundColor: meta.color }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Nikki callout */}
          {j.limiting_pillar_label && (
            <div className="mt-5 bg-zinc-900/60 border border-zinc-800 rounded-sm p-4 flex items-start gap-3" data-testid="journey-nikki-callout">
              <div className="w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${limitingColor}22`, color: limitingColor }}>
                <Sparkles className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="text-sm text-zinc-200 leading-snug">
                  <span className="font-bold">{j.limiting_pillar_label}</span> is your lever right now — that&apos;s what today&apos;s Focus aims at. Lift it and your whole Wellness IQ rises.
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <button onClick={() => navigate('/workouts')} className="inline-flex items-center gap-1.5 text-xs font-bold text-black bg-primary px-3 py-1.5 rounded-sm hover:bg-primary/90 transition-colors" data-testid="journey-start-focus">
                    <Activity className="w-3.5 h-3.5" /> Today&apos;s Focus <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => navigate('/chat')} className="inline-flex items-center gap-1.5 text-xs font-bold text-zinc-300 border border-zinc-700 px-3 py-1.5 rounded-sm hover:text-white hover:border-zinc-600 transition-colors" data-testid="journey-ask-nikki">
                    <MessageCircle className="w-3.5 h-3.5" /> Ask Nikki
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ADAPTATION WINS */}
        <div className="hpme-card p-6 overflow-hidden" data-testid="journey-wins">
          <div className="hpme-card-corner" />
          <h3 className="font-secondary font-bold text-lg tracking-tight mb-1">ADAPTATION WINS</h3>
          <p className="text-xs text-zinc-500 mb-4">We celebrate <span className="text-zinc-300">improvement</span> — better habits, not bigger numbers.</p>
          {j.wins.length > 0 ? (
            <div className="grid sm:grid-cols-2 gap-3">
              {j.wins.map((w, i) => {
                const Ic = WIN_ICONS[w.icon] || Award;
                return (
                  <div key={i} className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-4 flex items-start gap-3" data-testid={`journey-win-${i}`}>
                    <div className="w-9 h-9 rounded-sm bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
                      <Ic className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-secondary font-bold text-sm text-white leading-tight">{w.title}</p>
                      <p className="text-xs text-zinc-500 mt-0.5">{w.detail}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8" data-testid="journey-no-wins">
              <Sparkles className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">Your first wins are coming — check in daily and your improvements show up here.</p>
            </div>
          )}
        </div>

        {/* BADGES & MILESTONES */}
        {badges?.badges && (
          <BadgesSection badges={badges.badges} earnedCount={badges.earned_count} totalCount={badges.total_count} />
        )}
      </div>
    </DashboardLayout>
  );
};

export default Journey;
